import React, { useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { InputBaseProps } from '@material-ui/core';
import { TextField } from '../../../ui-kit';
import { Identified } from '../../../types';

export const FETCH_MORE_STRING = 'FETCH_MORE_STRING';
export const CREATE_NEW_STRING = 'CREATE_NEW_STRING';

interface AutocompleteSearchProps<T extends Identified> {
    onSearchResultClick(event: React.ChangeEvent<Record<string, unknown>>, value: T | null): void;
    searchQuery: string;
    value?: T | null;
    label?: string;
    onInputChange(value: string): void;
    options: T[];
    renderOption?(option: T): React.ReactNode;
    getOptionLabel?(option: T): string;
    isLoading?: boolean;
    onBlur?(event: React.FocusEvent<HTMLDivElement>): void;
    helpertext?: string;
    error?: boolean;
    disabled?: boolean;
    onClick?(): void;
    margin?: InputBaseProps['margin'];
    fullWidth?: boolean;
    showFetchMore?: boolean;
    onFetchMore?(): void;
    onCreateNew?(): void;
}

const getOptionSelected = (option: Identified, value: Identified) => option.id === value.id;

const AutocompleteSearch = <T extends Identified,>({
    searchQuery,
    onInputChange,
    onSearchResultClick,
    getOptionLabel,
    options,
    label,
    renderOption,
    value,
    isLoading,
    onBlur,
    helpertext,
    error,
    disabled,
    onClick,
    margin,
    fullWidth,
    onFetchMore,
    onCreateNew,
    showFetchMore,
}: AutocompleteSearchProps<T>) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleItemClick = (e: React.ChangeEvent<Record<string, unknown>>, v: T | null) => {
        if (v) {
            if (v.id === FETCH_MORE_STRING && onFetchMore) {
                return onFetchMore();
            }
            if (v.id === CREATE_NEW_STRING && onCreateNew) {
                return onCreateNew();
            }
        }
        onSearchResultClick(e, v);
        return setIsOpen(false);
    };

    const handleInputChange = (_e: unknown, v: string) => {
        onInputChange(v);
    };

    const onOpen = () => {
        setIsOpen(true);
    };

    const onClose = () => {
        setIsOpen(false);
    };

    let finalOptions = options;

    if (onCreateNew) {
        finalOptions = [{ id: CREATE_NEW_STRING } as T, ...options];
    }

    if (onFetchMore && showFetchMore) {
        finalOptions.push({ id: FETCH_MORE_STRING } as T);
    }

    return (
        <Autocomplete
            open={isOpen}
            onClose={onClose}
            onOpen={onOpen}
            fullWidth={fullWidth}
            value={value}
            size='small'
            disabled={disabled}
            onClick={onClick}
            onBlur={onBlur}
            disableCloseOnSelect
            loading={isLoading}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            onChange={handleItemClick}
            options={finalOptions}
            getOptionSelected={getOptionSelected}
            inputValue={searchQuery}
            clearOnBlur={false}
            onInputChange={handleInputChange}
            filterOptions={o => o}
            renderInput={(
                { InputProps: { className, ...restInputProps }, inputProps, InputLabelProps: _omitted, ...props }
            ) => (
                <TextField
                    {...restInputProps}
                    onClick={onClick}
                    helpertext={helpertext}
                    inputProps={inputProps}
                    label={label}
                    error={error}
                    margin={margin}
                    {...props}
                />
            )}
        />
    );
};

export default AutocompleteSearch;
